import React, {Component, ReactElement, ReactNode} from 'react'
import {
    ADMINISTORATOR_MODE,
    AVAILABLE_OR_NOT,
    DISASTER_TOPIC_CATEGORY,
    MESSAGE_TYPE,
    YES_OR_NOT
} from '../../../CodeMaster';
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage';
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin';
import {Nullable} from '../../../app/TypeHelpers';
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate';
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links';
import {Box, Table, TableBody, TableRow, TableCell} from '@mui/material';
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table';
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons';
import {Messages} from '../../../Messages';
import styled from 'styled-components';
import NatCondominiumRepo from '../condominium/repository/NatCondominiumRepo';
import {Colors} from '../../../styledComponents/Constants';
import {DeleteAlertIcon} from '../../../styledComponents/adminPage/IconCreator';
import NatDisasterRepo from './repository/NatDisasterRepo'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper
} from '../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {TopicInfo} from './repository/NatGetDisasterTopicForAdmin'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {judgeAuthorityCheck} from '../../common/util/Natutil'
import moment, {Moment} from 'moment'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {CommentInfo} from './repository/NatGetDisasterCommentForAdmin'
import {NatAdminDisasterCommentArea} from './admin/NatAdminDisasterCommentArea'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {Location} from 'react-router-dom'
import ReactDOM from 'react-dom';

interface Props {
    condominiumCd: string
    topicId: number
    commentId?: number
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface State {
    condominiumName: string
    topicInfo: TopicInfo
    comments: CommentInfo[]
    currentDateTime: Moment
    disasterStartDate: Nullable<string>
    isLoadCompleted: boolean
}

class NatAdminDisasterTopicPage extends Component<Props & AdminPagePropsSet, State> {
    mode: Nullable<string>;

    constructor(props: Props & AdminPagePropsSet) {
        super(props);
        this.mode = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.SELECTED_MODE)

        this.state = {
            condominiumName: '',
            topicInfo: {
                topicId: 0,
                condominiumCd: '',
                category: '',
                title: '',
                description: '',
                isPast: '',
                editAvailability: '',
                modifierRoleId: '',
                modifierFamilyName: '',
                modifierFirstName: '',
                registerDatetime: moment(),
                updateDatetime: moment(),
                version: 0
            },
            comments: [],
            currentDateTime: moment(),
            disasterStartDate: null,
            isLoadCompleted: false
        };

        this.onClickBackLink = this.onClickBackLink.bind(this);
        this.onClickEditButton = this.onClickEditButton.bind(this);
        this.onClickDeleteLink = this.onClickDeleteLink.bind(this);
        this.handleDeleteOkClick = this.handleDeleteOkClick.bind(this);
        this.handleDeleteCancelClick = this.handleDeleteCancelClick.bind(this);
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, condominiumRepo, eventEmitter} = this.props
        await this.props.eventEmitter.showLoading()
        this.showInitialMessage()
        try {
            // 全体モードの場合は物件名を表示するために物件情報を取得する
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                const condominiumResponse = await this.props.asyncCallFunction(
                    condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (condominiumResponse?.details) {
                    this.setState({
                        condominiumName: condominiumResponse.details.condominiumName
                    })
                }
            }

            const [currentDateTime, topicInfo, comments, disasterStartDate] = await Promise.all([
                this.getCurrentDateTime(),
                this.getDisasterTopic(),
                this.getDisasterComments(),
                this.getDisasterStartDate()
            ])

            if (topicInfo) {
                this.setState({
                    topicInfo: topicInfo,
                    comments: comments ?? [],
                    currentDateTime: moment(currentDateTime),
                    disasterStartDate: disasterStartDate
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true, state: {}})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    async getCurrentDateTime(): Promise<Nullable<string>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.currentDateTime ?? null
    }

    async getDisasterTopic(): Promise<Nullable<TopicInfo>> {
        const {condominiumCd, topicId, disasterRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.getAdminDisasterTopic.bind(disasterRepo, condominiumCd, topicId),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details ?? null
    }

    async getDisasterComments(): Promise<Nullable<CommentInfo[]>> {
        const {condominiumCd, topicId, disasterRepo, commentId, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.getDisasterCommentForAdmin.bind(
                disasterRepo, condominiumCd, topicId, commentId),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.commentInfo ?? null
    }

    async getDisasterStartDate(): Promise<Nullable<string>> {
        const {condominiumCd, disasterRepo, eventEmitter} = this.props

        // 災害時モード設定を取得する
        const response = await this.props.asyncCallFunction(
            disasterRepo.getDisasterMode.bind(disasterRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.disasterStartDate ?? null
    }


    fetchComments = async () => {
        const comments = await this.getDisasterComments()
        if (comments) {
            ReactDOM.flushSync(() => {
                this.setState({comments: comments})
            })
        }
    }

    onClickBackLink(): void {
        if (this.props.commentId) {
            this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/disasters/comments`)
        } else {
            this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/disasters/topics`)
        }
    }

    onClickEditButton(): void {
        const {condominiumCd, topicId} = this.props
        this.props.navigate(`/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}/edit`)
    }

    async onClickDeleteLink(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '削除確認',
                titleIcon: <DeleteAlertIcon/>,
                errorLevel: Messages.IDISC0001.errorLevel,
                message: Messages.IDISC0001.message,
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: '削除する',
                        buttonHandler: this.handleDeleteOkClick,
                        color: Colors.Alert
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleDeleteCancelClick,
                    },
                ],
            },
        );
    }

    handleDeleteCancelClick(): void {
        this.props.eventEmitter.closeDialog();
    }

    async handleDeleteOkClick(): Promise<void> {
        const {condominiumCd, topicId, disasterRepo, eventEmitter} = this.props
        const {topicInfo} = this.state

        this.props.eventEmitter.closeDialog();
        await this.props.eventEmitter.showLoading();
        try {

            const response = await this.props.asyncCallFunction(
                disasterRepo.deleteDisasterTopicForAdmin.bind(
                    disasterRepo, condominiumCd, topicId),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
            if (response?.requestError === null) {
                this.props.navigate(`/admin/condominiums/${condominiumCd}/disasters/topics`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0006.toString('トピック', topicInfo.title),
                            }
                        }
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderPageHeaderArea(): Nullable<ReactNode> {
        return (
            <>
                <AuthorityChecker
                    permittedAuthorities={HKConstants.CMN.AUTH.REGISTER_TOPIC_DISASTER_INFORMATION_SHARING}>
                    {this.state.topicInfo.editAvailability === AVAILABLE_OR_NOT.AVAILABLE.value &&
                        <PageFotterAreaLeft>
                            <AdminPrimaryButton
                                onClick={this.onClickEditButton}>
                                編集する
                            </AdminPrimaryButton>
                        </PageFotterAreaLeft>
                    }
                </AuthorityChecker>

                <AuthorityChecker
                    permittedAuthorities={HKConstants.CMN.AUTH.DELETE_TOPIC_DISASTER_INFORMATION_SHARING}>
                    <PageFotterAreaRight>
                        <DeleteLink
                            linkColor={Colors.Alert}
                            onClick={this.onClickDeleteLink}>
                            トピックを削除する
                        </DeleteLink>
                    </PageFotterAreaRight>
                </AuthorityChecker>
            </>
        )
    }

    renderTopicArea(): ReactNode {
        const {condominiumName, topicInfo} = this.state
        return (
            <Table>
                <StyledTableCaption>トピック詳細</StyledTableCaption>
                <TableBody>
                    {this.mode === ADMINISTORATOR_MODE.ALL.value &&
                        <TableRow>
                            <TableRowName>物件名</TableRowName>
                            <TableCell>
                                {condominiumName}
                            </TableCell>
                        </TableRow>
                    }
                    <TableRow>
                        <TableRowName>カテゴリ</TableRowName>
                        <TableCell>
                            {DISASTER_TOPIC_CATEGORY.codeToEnumConstants(topicInfo.category)?.label}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>タイトル</TableRowName>
                        <TableCell>
                            {topicInfo.title}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>説明文</TableRowName>
                        <TableCellWordBreakAll>
                            {topicInfo.description}
                        </TableCellWordBreakAll>
                    </TableRow>
                    <TableRow>
                        <TableRowName>更新者</TableRowName>
                        <TableCell>
                            {topicInfo.modifierRoleId
                                ? topicInfo.modifierFamilyName + ' ' + topicInfo.modifierFirstName
                                : '標準トピック'}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>更新日時</TableRowName>
                        <TableCell>
                            {moment(topicInfo.updateDatetime).format('YYYY-MM-DD HH:mm:ss')}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        )
    }

    renderCommentArea(): ReactNode {
        const {condominiumCd, topicId, commentId, disasterRepo, eventEmitter, asyncCallFunction} = this.props
        const {topicInfo, comments, disasterStartDate, currentDateTime} = this.state
        return (
            <Table>
                <StyledTableCaption>コメント</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableCell>
                            <NatAdminDisasterCommentArea
                                condominiumCd={condominiumCd}
                                topicId={topicId}
                                CommentInfoList={comments}
                                pastFlag={topicInfo.isPast === YES_OR_NOT.YES.value}
                                commentId={commentId}
                                currentDateTime={currentDateTime}
                                disasterRepo={disasterRepo}
                                onPostSuccess={this.fetchComments}
                                eventEmitter={eventEmitter}
                                asyncCallFunction={asyncCallFunction}
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        )
    }

    renderPageFooterArea(): ReactNode {
        return (
            <PageFotterAreaLeft>
                <BackLink
                    onClick={this.onClickBackLink}
                >
                    災害時情報共有一覧へ戻る
                </BackLink>
            </PageFotterAreaLeft>
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }

        return (
            <>
                {(judgeAuthorityCheck(HKConstants.CMN.AUTH.REGISTER_TOPIC_DISASTER_INFORMATION_SHARING) ||
                        judgeAuthorityCheck(HKConstants.CMN.AUTH.DELETE_TOPIC_DISASTER_INFORMATION_SHARING)) &&
                    !this.props.commentId &&
                    <Box mb={'15px'}>
                        <PageFooterArea>
                            {this.renderPageHeaderArea()}
                        </PageFooterArea>
                    </Box>
                }
                <PageMainArea>
                    {this.renderTopicArea()}
                    {this.renderCommentArea()}
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>

        )
    }
}

export default createAdminPage(NatAdminDisasterTopicPage)

const TableCellWordBreakAll = styled(TableCell)`
    &.MuiTableCell-root {
        word-break: break-all;
        white-space: pre-wrap;
    }
`
